import {Datepicker} from "flowbite-datepicker";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {ajaxCreateBookingPeriod} from "./booking";
import utils from "../utils/utils";
import {createDatepickerLocale} from "../services/dashboard-locale";
import {getTranslation, setSingleParameter} from "./helpers";

dayjs.extend(customParseFormat);

/**
 * Datovælgeren.
 *
 * Afløser daterangepicker + moment. Begge er ude: daterangepicker krævede
 * moment, og moment har været i vedligeholdelsestilstand siden 2020.
 *
 * HVORFOR VI SELV EJER INTERVALLOGIKKEN
 * Flowbites egen DateRangePicker kræver TO inputfelter — den afviser med
 * `inputs.length < 2`. Vores markup har ét felt, der viser "start - slut", og
 * hele URL-synkroniseringen læser det ene felt. At skifte til to felter ville
 * ændre brugerfladen på hver eneste dashboardskærm. Derfor kører vi
 * flowbite-datepickers ENKELT-kalender og holder selv styr på start og slut.
 * Det er de ~60 linjer nedenfor, og de ligger ét sted.
 *
 * Kaldsstederne rører sig ikke: de fik dette API i forrige commit, og
 * onSelect får stadig datoobjekter med .format()/.startOf()/.endOf()/.unix() —
 * nu dayjs i stedet for moment. Samme metoder, samme opførsel.
 */

/* Feltets visningsformat. Vi bruger kun to, så en tabel slår en oversætter:
   moment-tokens til venstre, flowbite-datepickers til højre. */
const FORMATS = {
    "DD/M YYYY": "dd/m yyyy",
    "DD.MM.YYYY": "dd.mm.yyyy",
};
const DEFAULT_FORMAT = "DD/M YYYY";
const SEPARATOR = " - ";

/* Tilstand pr. felt. WeakMap frem for data-attribut, så intet lækker til DOM
   og indgangene ryddes af sig selv når elementet forsvinder. */
const state = new WeakMap();

/* Vores egne hændelsesnavne. Før lyttede boot-page.js direkte efter
   `show.daterangepicker`/`apply.daterangepicker`, altså bibliotekets egne —
   de forsvandt lydløst med biblioteket. */
export const DATE_RANGE_EVENTS = {
    open: "ng:daterange:open",
    select: "ng:daterange:select",
};

const toDayjs = (value) => (value && dayjs.isDayjs(value) ? value : dayjs(value));

function displayValue(start, end, momentFormat, single) {
    const fmt = momentFormat || DEFAULT_FORMAT;
    if (single || !end) {
        return start ? start.format(fmt) : "";
    }
    return `${start.format(fmt)}${SEPARATOR}${end.format(fmt)}`;
}

/**
 * Sæt en vælger op på et felt.
 *
 * @param {string|jQuery} target      Element eller selektor.
 * @param {Object}        options     maxDate, minDate, startDate, endDate,
 *                                    single, maxSpanDays, weekdaysOnly, format.
 * @param {Function}      [onSelect]  (start, end) med dayjs-objekter.
 * @returns {jQuery} Elementet, så kaldsstedet kan kæde videre.
 */
export function initDateRange(target, options = {}, onSelect) {
    const {
        maxDate = false,
        minDate = null,
        startDate = null,
        endDate = null,
        single = false,
        maxSpanDays = null,
        weekdaysOnly = false,
        format = null,
    } = options;

    const $el = target instanceof $ ? target : $(target);
    if (!$el.length) {
        return $el;
    }

    const el = $el.get(0);
    destroyDateRange($el);

    const language = utils.get_locale().replace('_', '-');
    Datepicker.locales[language] = createDatepickerLocale(
        language, getTranslation('today'), getTranslation('clear')
    );

    const pickerOptions = {
        format: FORMATS[format || DEFAULT_FORMAT] || FORMATS[DEFAULT_FORMAT],
        language,
        weekStart: 1,
        autohide: single,
        todayBtn: false,
        clearBtn: false,
    };
    if (minDate) pickerOptions.minDate = toDayjs(minDate).toDate();
    if (maxDate) pickerOptions.maxDate = toDayjs(maxDate).toDate();
    if (weekdaysOnly) pickerOptions.daysOfWeekDisabled = [0, 6];

    const picker = new Datepicker(el, pickerOptions);

    // Kalenderen placeres af biblioteket med INLINE top/left. Den kommer med
    // Tailwind-klasserne `top-0 left-0`, og fordi utilities her importeres med
    // `important`, slår de inline-stilen — inline taber altid til !important.
    // Uden det her lander kalenderen i sidens øverste venstre hjørne i stedet
    // for under feltet. Samme fælde som at `hidden` bliver display:none
    // !important; se noten om det i afsnit 1 af dashboard-ui.css.
    //
    // `z-50` hører til SAMME fælde og blev overset. Biblioteket er bygget til
    // Tailwind og sætter sit lag som utility-klasse, ikke som CSS. Inde i en
    // popup betyder det, at kalenderen tegnes BAG kortet: .fancybox__container
    // ligger på 1050, og §"the calendar is appended to <body>" i
    // dashboard-ui.css hæver derfor .datepicker til 1100 — men den regel er
    // ULAGDELT, og en lagdelt !important-utility slår alt ulagdelt uanset
    // specificitet. Målt 20/8 på et åbent felt: computed z-index 50, ikke
    // 1100, og kalenderen lå under .fancybox__slide.
    //
    // Klassen fjernes derfor i stedet for at blive overskrevet — at kæmpe mod
    // en lagdelt !important med endnu et !important kan ikke vindes.
    const dropdown = picker.picker && picker.picker.element;
    if (dropdown) {
        dropdown.classList.remove("top-0", "left-0", "z-50");
    }

    const entry = {
        picker,
        single,
        maxSpanDays,
        format: format || DEFAULT_FORMAT,
        start: startDate ? toDayjs(startDate) : null,
        end: endDate ? toDayjs(endDate) : null,
        onSelect,
    };
    state.set(el, entry);

    if (entry.start) {
        el.value = displayValue(entry.start, entry.end, entry.format, single);
    }

    // Intervallet: første valg sætter start, næste sætter slut. Et tredje valg
    // begynder forfra — samme rytme som den gamle vælger.
    entry.handler = (event) => {
        const picked = dayjs(event.detail && event.detail.date ? event.detail.date : picker.getDate());
        if (!picked.isValid()) {
            return;
        }

        if (entry.single) {
            entry.start = picked;
            entry.end = picked;
        } else if (!entry.start || entry.end) {
            entry.start = picked;
            entry.end = null;
        } else if (picked.isBefore(entry.start)) {
            entry.start = picked;
            entry.end = null;
        } else {
            entry.end = picked;
            if (entry.maxSpanDays) {
                const span = entry.end.diff(entry.start, "day");
                if (span > entry.maxSpanDays - 1) {
                    entry.end = entry.start.add(entry.maxSpanDays - 1, "day");
                }
            }
        }

        el.value = displayValue(entry.start, entry.end, entry.format, entry.single);

        // Sat af setDateRange: værdien kom fra koden, ikke fra brugeren.
        if (entry.stille) {
            return;
        }

        // Meld først når valget er komplet, som den gamle vælgers autoApply.
        if (entry.single || entry.end) {
            if (typeof entry.onSelect === "function") {
                entry.onSelect(entry.start, entry.end || entry.start);
            }
            $el.trigger(DATE_RANGE_EVENTS.select, [entry.start, entry.end || entry.start]);
            if (!entry.single) {
                picker.hide();
            }
        }
    };

    el.addEventListener("changeDate", entry.handler);

    // Egne hændelser, så resten af appen ikke skal kende bibliotekets navne.
    // boot-page.js lytter efter dem for at slå "brugerdefineret periode" til.
    entry.showHandler = () => $el.trigger(DATE_RANGE_EVENTS.open);
    el.addEventListener("show", entry.showHandler);

    /* SLUTDATOEN FORSVANDT, naar man klikkede uden for vaelgeren.
       Biblioteket kender kun ÉN dato — det er os, der holder start og slut
       — og naar panelet lukkes, skriver det sin egen formatering tilbage i
       feltet. Saa stod der "01/08 2026" paa et interval, brugeren lige
       havde valgt til 12/08.

       Vi genskriver derfor vores egen visning, naar panelet lukker og naar
       feltet mister fokus. Det er ren visning: entry.start/entry.end er
       uroerte, og perioden var allerede meldt videre af handleren.
       (Mads 21/8 — set i produktion.) */
    entry.restoreHandler = () => {
        if (!entry.start) {
            return;
        }
        const vist = displayValue(entry.start, entry.end, entry.format, entry.single);
        if (el.value !== vist) {
            el.value = vist;
        }
    };
    el.addEventListener("hide", entry.restoreHandler);
    el.addEventListener("blur", entry.restoreHandler);

    /* ÉN DAG AD GANGEN — dobbeltklik (Mads 24/8).

       Man kunne ikke vælge en enkelt dag. Klikker man samme dato to gange,
       udsender biblioteket INTET valg anden gang: det kender kun én dato,
       og den er allerede sat. Vores handler ovenfor blev derfor aldrig
       kaldt, `entry.end` forblev null, og siden genindlæste ikke. Målt i
       produktion 24/8: feltet viste "12/8 2026" mens URL'en stod på juli,
       og panelet blev stående.

       Derfor lytter vi på cellerne selv. `data-date` er epoch-ms for dagens
       lokale midnat — bibliotekets egen kilde, ikke en tekst vi tolker.

       Dobbeltklik, ikke "klik ved siden af": at klikke uden for panelet er
       også gestussen for at fortryde, og de to må ikke betyde det samme.
       (Mads valgte den 24/8 blandt tre.) */
    if (dropdown) {
        entry.dblHandler = (event) => {
            const celle = event.target.closest(".datepicker-cell.day");
            if (!celle || celle.classList.contains("disabled")) {
                return;
            }
            const raa = Number(celle.getAttribute("data-date"));
            if (!Number.isFinite(raa)) {
                return;
            }
            const dag = dayjs(raa);
            if (!dag.isValid()) {
                return;
            }

            entry.start = dag;
            entry.end = dag;
            el.value = displayValue(entry.start, entry.end, entry.format, entry.single);
            if (entry.stille) {
                return;
            }
            if (typeof entry.onSelect === "function") {
                entry.onSelect(entry.start, entry.end);
            }
            $el.trigger(DATE_RANGE_EVENTS.select, [entry.start, entry.end]);
            picker.hide();
        };
        dropdown.addEventListener("dblclick", entry.dblHandler);
    }

    return $el;
}

/**
 * Sæt valgt periode og opdatér feltets viste tekst.
 *
 * @param {string|jQuery} target
 * @param {Object} start dayjs eller noget dayjs kan læse.
 * @param {Object} [end] Udelades ved enkeltdato.
 * @returns {boolean} Falsk hvis der ikke er nogen vælger på elementet.
 */
export function setDateRange(target, start, end = null) {
    const $el = target instanceof $ ? target : $(target);
    const el = $el.get(0);
    const entry = el ? state.get(el) : null;
    if (!entry) {
        return false;
    }

    entry.start = toDayjs(start);
    entry.end = end ? toDayjs(end) : entry.start;

    el.value = displayValue(entry.start, entry.end, entry.format, entry.single);
    /* PROGRAMMATISK værdisætning må ikke udgive sig for at være et brugervalg:
       picker.setDate() fyrer changeDate, som kaldte onSelect — og på Samlet
       endte det i en EKSTRA hentning af hele gitteret, oveni den kaldelsen
       selv udløste (målt 17/8: to bookings_html pr. periodeskift, ~1,3 sek.
       fra hinanden, med loaderen tændt-slukket-tændt imellem).
       Flaget ryddes i næste tick, så det også dækker en asynkron changeDate;
       et menneske kan ikke nå at vælge en dato inden da. */
    entry.stille = true;
    entry.picker.setDate(entry.start.toDate(), {render: false});
    setTimeout(() => {
        entry.stille = false;
    }, 0);

    return true;
}

/**
 * Fjern vælgeren og dens hændelse fra et element.
 *
 * Bruges hvor felter genskabes — kundepopuppens bookingperiode åbner samme
 * felt igen og igen, og uden oprydning stables bindingerne.
 *
 * @param {string|jQuery} target
 */
export function destroyDateRange(target) {
    const $el = target instanceof $ ? target : $(target);
    const el = $el.get(0);
    if (!el) {
        return;
    }

    const entry = state.get(el);
    if (!entry) {
        return;
    }

    if (entry.handler) {
        el.removeEventListener("changeDate", entry.handler);
    }
    if (entry.showHandler) {
        el.removeEventListener("show", entry.showHandler);
    }
    if (entry.restoreHandler) {
        el.removeEventListener("hide", entry.restoreHandler);
        el.removeEventListener("blur", entry.restoreHandler);
        /* Dropdownen rives ned af picker.destroy(), men lytteren fjernes
           eksplicit: destroyDateRange kaldes ogsaa ved re-init paa samme
           felt, og to instanser ville ellers begge svare paa ét dobbeltklik. */
        if (entry.dblHandler && entry.picker && entry.picker.picker &&
            entry.picker.picker.element) {
            entry.picker.picker.element.removeEventListener("dblclick", entry.dblHandler);
        }
    }
    if (entry.picker && typeof entry.picker.destroy === "function") {
        entry.picker.destroy();
    }
    state.delete(el);
}

export function togglePeriod(e) {
    const _this = $(e.currentTarget);
    if (_this.is(":checked")) {
        $("body").addClass("custom-period");
    } else {
        $("body").removeClass("custom-period");
    }
}

export function dateRangePickerWeek() {
    initDateRange('input[name="dateRangeWeek"]', {
        maxDate: false,
        minDate: dayjs(),
        weekdaysOnly: true,
        maxSpanDays: 5,
    }, (start, end) => {
        const startDate = start.format("YYYY-MM-DD");
        const endDate = end.format("YYYY-MM-DD");

        setSingleParameter("start", startDate);
        setSingleParameter("end", endDate);
        ajaxCreateBookingPeriod(startDate, endDate);
    });
}
